import { useEffect, useState } from "react";
import API_CONSTRUCTOR from "../../models/api-model";
import PropertyAPIs from "../../models/property";
import { get, orderBy, result } from "lodash";
import { Box, Skeleton, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { PropertyCard } from "../home";
import { COLORS } from "../../utils/colors";
const apiModel = API_CONSTRUCTOR("https://api.propertyreach.com");
const propertyApi = new PropertyAPIs(apiModel);

export const NearbyInsights = ({
  property = {},
  propertyId,
  setMarketValuePerSqft = () => {},
}) => {
  const [nearbyInsights, setNearbyInsights] = useState([]);
  const [isLoadingNearbyInsights, setIsLoadingNearbyInsights] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (propertyId) {
      setIsLoadingNearbyInsights(true);
      setError(null);
      setNearbyInsights([]);

      propertyApi
        .getNearbyInsights(propertyId)
        .then((result) => {
          const { estimatedValue = 0, squareFeet = 0 } = { ...property };
          const pricePerSquareFeet =
            estimatedValue && squareFeet
              ? estimatedValue / squareFeet
              : Infinity;

          const properties = get(result, "properties", null);
          const meta = get(result, "meta", {});
          const { status } = meta;

          if (properties && properties.length) {
            // Step 1: Deduplicate by streetAddress
            const uniqueByAddress = new Map();
            properties.forEach((prop) => {
              if (!uniqueByAddress.has(prop.streetAddress)) {
                uniqueByAddress.set(prop.streetAddress, prop);
              }
            });
            let filteredProperties = Array.from(uniqueByAddress.values());

            // Step 2: Sort by estimatedValue desc
            filteredProperties = orderBy(
              filteredProperties,
              ["estimatedValue"],
              ["desc"]
            );

            // Step 3: Set the final data
            setNearbyInsights(filteredProperties);

            // Step 4: Compute market value per sqft for top 10%
            let totalProperties = 0;
            let totalPropertiesValue = 0;
            let totalPropertiesSqFt = 0;

            filteredProperties.forEach((property) => {
              const { estimatedValue = 0, squareFeet = 0 } = property;
              if (totalProperties < filteredProperties.length / 10) {
                totalProperties += 1;
                totalPropertiesValue += estimatedValue || 0;
                totalPropertiesSqFt += squareFeet || 0;
              }
            });

            if (totalProperties > 1) {
              setMarketValuePerSqft(
                Math.ceil(totalPropertiesValue / totalPropertiesSqFt)
              );
            }
          }

          if (status !== 200) {
            const { message } = meta;
            setError(message);
          }
        })
        .catch((error) => {
          console.error("Error fetching nearby property details:", error);
        })
        .finally(() => {
          setIsLoadingNearbyInsights(false);
        });
    }
  }, [propertyId]);

  if (isLoadingNearbyInsights) {
    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 2,
          marginTop: 2,
        }}
      >
        {[...Array(3)].map((_, index) => (
          <Box height={300} key={index} width={345}>
            <Skeleton animation="wave" height={300} key={index} width={345} />
          </Box>
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
      <Box
        display={"flex"}
        flexDirection={"column"}
        alignItems={"center"}
        sx={{ maxWidth: 1200 }}
      >
        <div className="text-center mb-10">
          <h3 className="text-4xl font-bold text-gray-800 mb-2">
            Similar Properties
          </h3>
        </div>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 2,
            marginTop: 2,
          }}
        >
          {nearbyInsights.map((property) => {
            return (
              <PropertyCard
                imageHeight={100}
                key={property.id}
                property={property}
                showImageModalOnClick={true}
                showPrices={true}
                target={"blank"}
                width={200}
              />
            );
          })}
          {!nearbyInsights || !nearbyInsights.length ? (
            <Typography variant="body2">No nearby properties found</Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};
